import { format, isValid, parse } from 'date-fns';

// Noon reference avoids DST edge cases at midnight.
// Using parse() with a fixed format instead of parseISO() prevents timezone-related off-by-one issues:
// parseISO('2022-12-02') creates midnight UTC, which in UTC+14 would give Dec 3.
const REFERENCE_DATE = new Date(2000, 0, 1, 12, 0, 0);

const getDatePart = (inputString: string): string => (inputString.includes('T') ? inputString.split('T')[0] : inputString);

export const parseDate = (inputString: string): Date => parse(getDatePart(inputString), 'yyyy-MM-dd', REFERENCE_DATE);

export const isParseableAsDate = (inputString: string): boolean => {
  const datePart = getDatePart(inputString);
  const parsed = parse(datePart, 'yyyy-MM-dd', REFERENCE_DATE);

  return isValid(parsed) && format(parsed, 'yyyy-MM-dd') === datePart;
};

export const isValidDate = (date: string): boolean => isParseableAsDate(date);

const momentToDateFnsTokens: Record<string, string> = {
  DD: 'dd',
  MMMM: 'MMMM',
  MMM: 'MMM',
  MM: 'MM',
  YYYY: 'yyyy',
  YY: 'yy'
};

const convertMomentFormat = (momentFormat: string): string =>
  momentFormat.replace(/YYYY|MMMM|MMM|MM|DD|YY/g, (match) => momentToDateFnsTokens[match] || match);

export const parseDateWithFormat = (dateString: string, momentFormatString: string): Date | null => {
  const dateFnsFormat = convertMomentFormat(momentFormatString);
  // Normalize separators to match — moment was lenient about mismatched separators (e.g. '/' vs '-')
  const separator = dateFnsFormat.match(/[^a-zA-Z]/)?.[0] || '-';
  const normalizedDate = dateString.replace(/[/\-.]/g, separator);
  const parsed = parse(normalizedDate, dateFnsFormat, REFERENCE_DATE);

  return isValid(parsed) ? parsed : null;
};
